import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useSupabaseClient from '../hooks/useSupabaseClient';
import InvoiceAccountingEntryPickerModal from './InvoiceAccountingEntryPickerModal';
import DraggableDialogPaper from './DraggableDialogPaper';

const createUiId = () => {
  try {
    return globalThis?.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  } catch {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

const emptyLine = () => ({
  _uiId: createUiId(),
  entry_date: new Date().toISOString().split('T')[0],
  account_code: '',
  account_name: '',
  description: '',
  debit: 0,
  credit: 0,
  third_party_nit: '',
  third_party_name: '',
  cost_center_code: '',
  document_reference: '',
  base_amount: null,
  tax_rate: null,
  observation: '',
  invoice_id: null,
  source_accounting_entry_id: null
});

const formatEsNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num);
};

const parseEsNumber = (raw) => {
  if (raw === null || raw === undefined) return 0;
  const s = String(raw).trim();
  if (!s) return 0;

  const normalized = s
    .replace(/\s/g, '')
    .replace(/[^0-9,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const FormattedNumberField = ({ value, onChangeNumber, disabled, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [display, setDisplay] = useState(formatEsNumber(value));

  useEffect(() => {
    if (isFocused) return;
    setDisplay(formatEsNumber(value));
  }, [value, isFocused]);

  return (
    <TextField
      {...props}
      size="small"
      type="text"
      inputMode="decimal"
      value={isFocused ? display : formatEsNumber(value)}
      onFocus={() => {
        setIsFocused(true);
        setDisplay(value === null || value === undefined ? '' : String(value));
      }}
      onBlur={() => {
        setIsFocused(false);
        const parsed = parseEsNumber(display);
        onChangeNumber(parsed);
      }}
      onChange={(e) => setDisplay(e.target.value)}
      disabled={disabled}
    />
  );
};

const InvoiceAccountingExampleEditorModal = ({ open, onClose, example, userId, onSaved }) => {
  const { supabase } = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeAccountRow, setActiveAccountRow] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState([]);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);

  const [header, setHeader] = useState({
    title: '',
    context: '',
    reason: '',
    tags: '',
    document_type: '',
    document_reference: '',
    source_invoice_id: ''
  });

  const [lines, setLines] = useState([emptyLine()]);

  const totals = useMemo(() => {
    const debit = lines.reduce((acc, l) => acc + (Number(l.debit) || 0), 0);
    const credit = lines.reduce((acc, l) => acc + (Number(l.credit) || 0), 0);
    return { debit, credit, diff: debit - credit };
  }, [lines]);

  useEffect(() => {
    if (!open) return;

    setError('');

    if (example) {
      setHeader({
        title: example.title || '',
        context: example.context || '',
        reason: example.reason || '',
        tags: Array.isArray(example.tags) ? example.tags.join(', ') : '',
        document_type: example.document_type || '',
        document_reference: example.document_reference || '',
        source_invoice_id: example.source_invoice_id ?? ''
      });

      const loadLines = async () => {
        setIsLoading(true);
        try {
          const { data, error: qErr } = await supabase
            .from('invoice_accounting_example_entries')
            .select('*')
            .eq('example_id', example.id)
            .eq('user_id', userId)
            .order('id', { ascending: true });

          if (qErr) throw qErr;
          setLines((data && data.length > 0)
            ? data.map((row) => ({ ...row, _uiId: row._uiId || createUiId() }))
            : [emptyLine()]
          );
        } catch (e) {
          setError(e.message || 'Error cargando líneas');
          setLines([emptyLine()]);
        } finally {
          setIsLoading(false);
        }
      };

      loadLines();
    } else {
      setHeader({
        title: '',
        context: '',
        reason: '',
        tags: '',
        document_type: '',
        document_reference: '',
        source_invoice_id: ''
      });
      setLines([emptyLine()]);
    }
  }, [open, example, supabase, userId]);

  useEffect(() => {
    const doSearch = async () => {
      if (!open) return;
      if (!supabase) return;
      if (!userId) return;
      if (activeAccountRow === null || activeAccountRow === undefined) return;

      const term = String(accountSearch || '').trim();
      if (!term) {
        setAccountOptions([]);
        return;
      }

      setIsSearchingAccounts(true);
      try {
        const queries = [];
        const parsed = Number.parseInt(term, 10);
        if (Number.isFinite(parsed)) {
          queries.push(
            supabase
              .from('invoice_chart_of_accounts')
              .select('account_code,description')
              .eq('user_id', userId)
              .eq('account_code', parsed)
              .limit(10)
          );
        }

        queries.push(
          supabase
            .from('invoice_chart_of_accounts')
            .select('account_code,description')
            .eq('user_id', userId)
            .ilike('description', `%${term}%`)
            .limit(25)
        );

        const results = await Promise.all(queries);
        const merged = [];
        const seen = new Set();
        for (const r of results) {
          if (r?.error) continue;
          for (const row of r?.data || []) {
            const key = String(row.account_code);
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(row);
          }
        }
        setAccountOptions(merged);
      } finally {
        setIsSearchingAccounts(false);
      }
    };

    const t = setTimeout(doSearch, 250);
    return () => clearTimeout(t);
  }, [open, supabase, userId, activeAccountRow, accountSearch]);

  const fetchAccountDescription = async (accountCode) => {
    if (!accountCode) return '';
    const parsed = parseInt(accountCode, 10);
    if (!Number.isFinite(parsed)) return '';

    const { data, error: qErr } = await supabase
      .from('invoice_chart_of_accounts')
      .select('description')
      .eq('account_code', parsed)
      .eq('user_id', userId)
      .single();

    if (qErr) return '';
    return data?.description || '';
  };

  const updateLine = (idx, patch) => {
    setLines(prev => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const removeLine = (idx) => {
    setLines(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [emptyLine()];
    });
  };

  const addLine = () => {
    setLines(prev => [...prev, emptyLine()]);
  };

  const handleAddFromPicker = (mapped) => {
    setActiveAccountRow(null);
    setAccountSearch('');
    setAccountOptions([]);
    // Importante: dejar que el Autocomplete (Popper/Portal) cierre antes de re-renderizar
    // una lista de filas distinta, para evitar errores DOM tipo removeChild.
    setTimeout(() => {
      setLines(prev => {
        const base = prev.length === 1 && !prev[0].account_code && !prev[0].account_name && (Number(prev[0].debit) || 0) === 0 && (Number(prev[0].credit) || 0) === 0;
        const next = (base ? [] : prev);
        return [...next, ...mapped.map(m => ({ ...emptyLine(), ...m, _uiId: createUiId() }))];
      });
    }, 0);
  };

  const validate = () => {
    if (!userId) return 'Usuario no autenticado';
    if (!header.title || !header.title.trim()) return 'El título es obligatorio';

    for (const [i, l] of lines.entries()) {
      if (!l.account_code || !String(l.account_code).trim()) return `Falta account_code en la línea ${i + 1}`;
      if (!l.account_name || !String(l.account_name).trim()) return `Falta account_name en la línea ${i + 1}`;
      const debit = Number(l.debit) || 0;
      const credit = Number(l.credit) || 0;
      if (debit === 0 && credit === 0) return `El débito o crédito debe ser > 0 en la línea ${i + 1}`;
    }

    return '';
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const tags = header.tags
        ? header.tags.split(',').map(t => t.trim()).filter(Boolean)
        : null;

      const payload = {
        user_id: userId,
        title: header.title.trim(),
        context: header.context || null,
        reason: header.reason || null,
        tags,
        document_type: header.document_type || null,
        document_reference: header.document_reference || null,
        source_invoice_id: header.source_invoice_id === '' ? null : Number(header.source_invoice_id)
      };

      let savedExample;

      if (example?.id) {
        const { data, error: upErr } = await supabase
          .from('invoice_accounting_examples')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', example.id)
          .eq('user_id', userId)
          .select()
          .single();

        if (upErr) throw upErr;
        savedExample = data;
      } else {
        const { data, error: insErr } = await supabase
          .from('invoice_accounting_examples')
          .insert(payload)
          .select()
          .single();

        if (insErr) throw insErr;
        savedExample = data;
      }

      const { error: delErr } = await supabase
        .from('invoice_accounting_example_entries')
        .delete()
        .eq('example_id', savedExample.id)
        .eq('user_id', userId);

      if (delErr) throw delErr;

      const entriesToInsert = lines.map(l => {
        const { id: _id, _uiId, created_at: _c, updated_at: _u, ...rest } = l;
        return {
          ...rest,
          example_id: savedExample.id,
          user_id: userId,
          invoice_id: rest.invoice_id === '' ? null : (rest.invoice_id ?? null),
          debit: Number(rest.debit) || 0,
          credit: Number(rest.credit) || 0,
          base_amount: rest.base_amount === '' ? null : (rest.base_amount ?? null),
          tax_rate: rest.tax_rate === '' ? null : (rest.tax_rate ?? null),
          updated_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        };
      });

      const { error: insEntriesErr } = await supabase
        .from('invoice_accounting_example_entries')
        .insert(entriesToInsert);

      if (insEntriesErr) throw insEntriesErr;

      onSaved?.(savedExample);
      onClose();
    } catch (e) {
      setError(e.message || 'Error guardando');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperComponent={DraggableDialogPaper}>
      <DialogTitle className="draggable-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'move' }}>
        <Box>
          <Typography variant="h6">{example ? 'Editar ejemplo contable' : 'Nuevo ejemplo contable'}</Typography>
          <Typography variant="caption" color="text.secondary">
            Débitos: {totals.debit.toLocaleString('es-CO')} | Créditos: {totals.credit.toLocaleString('es-CO')}
          </Typography>
          {totals.diff !== 0 && (
            <Chip
              sx={{ ml: 1 }}
              size="small"
              color="warning"
              label={`Descuadre: ${totals.diff.toLocaleString('es-CO')}`}
            />
          )}
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField
            label="Título"
            value={header.title}
            onChange={(e) => setHeader(prev => ({ ...prev, title: e.target.value }))}
            required
            disabled={isLoading}
          />
          <TextField
            label="Tags (separadas por coma)"
            value={header.tags}
            onChange={(e) => setHeader(prev => ({ ...prev, tags: e.target.value }))}
            disabled={isLoading}
          />
          <TextField
            label="Contexto (qué se está contabilizando)"
            value={header.context}
            onChange={(e) => setHeader(prev => ({ ...prev, context: e.target.value }))}
            multiline
            minRows={3}
            disabled={isLoading}
          />
          <TextField
            label="Razón / Por qué"
            value={header.reason}
            onChange={(e) => setHeader(prev => ({ ...prev, reason: e.target.value }))}
            multiline
            minRows={3}
            disabled={isLoading}
          />
          <TextField
            label="Tipo documento (opcional)"
            value={header.document_type}
            onChange={(e) => setHeader(prev => ({ ...prev, document_type: e.target.value }))}
            disabled={isLoading}
          />
          <TextField
            label="Documento referencia (opcional)"
            value={header.document_reference}
            onChange={(e) => setHeader(prev => ({ ...prev, document_reference: e.target.value }))}
            disabled={isLoading}
          />
          <TextField
            label="source_invoice_id (opcional)"
            value={header.source_invoice_id}
            onChange={(e) => setHeader(prev => ({ ...prev, source_invoice_id: e.target.value }))}
            disabled={isLoading}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6">Líneas</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setPickerOpen(true)}
              disabled={isLoading}
            >
              Copiar asientos
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addLine}
              disabled={isLoading}
            >
              Agregar línea
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Cuenta</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Débito</TableCell>
                <TableCell align="right">Crédito</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, idx) => {
                const rowKey = l?._uiId || String(l?.id ?? idx);
                return (
                <TableRow key={rowKey} hover>
                  <TableCell sx={{ minWidth: 120 }}>
                    <TextField
                      type="date"
                      size="small"
                      value={l.entry_date ? new Date(l.entry_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                      onChange={(e) => updateLine(idx, { entry_date: e.target.value })}
                      disabled={isLoading}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      disablePortal
                      blurOnSelect
                      options={accountOptions}
                      loading={isSearchingAccounts}
                      inputValue={activeAccountRow === rowKey ? accountSearch : (l.account_code ? String(l.account_code) : '')}
                      value={null}
                      onOpen={() => {
                        setActiveAccountRow(rowKey);
                        setAccountSearch(l.account_code ? String(l.account_code) : '');
                      }}
                      onClose={() => {
                        if (activeAccountRow === rowKey) {
                          setActiveAccountRow(null);
                          setAccountSearch('');
                        }
                      }}
                      onInputChange={(_e, nextValue, reason) => {
                        if (reason !== 'input') return;
                        setActiveAccountRow(rowKey);
                        setAccountSearch(nextValue);
                        updateLine(idx, { account_code: nextValue, account_name: '' });
                      }}
                      onChange={async (_e, opt) => {
                        if (!opt) return;
                        const code = opt.account_code !== null && opt.account_code !== undefined ? String(opt.account_code) : '';
                        const desc = opt.description || '';
                        updateLine(idx, { account_code: code, account_name: desc });
                        setActiveAccountRow(null);
                        setAccountSearch('');
                      }}
                      getOptionLabel={(opt) => {
                        if (!opt) return '';
                        const code = opt.account_code !== null && opt.account_code !== undefined ? String(opt.account_code) : '';
                        return `${code} - ${opt.description || ''}`;
                      }}
                      isOptionEqualToValue={(opt, val) => String(opt?.account_code) === String(val?.account_code)}
                      disabled={isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          onBlur={async () => {
                            const desc = await fetchAccountDescription(l.account_code);
                            updateLine(idx, { account_name: desc });
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <TextField
                      size="small"
                      value={l.account_name}
                      disabled
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 320 }}>
                    <TextField
                      size="small"
                      value={l.description || ''}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      disabled={isLoading}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>
                    <FormattedNumberField
                      value={l.debit ?? 0}
                      onChangeNumber={(n) => updateLine(idx, { debit: n })}
                      disabled={isLoading}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>
                    <FormattedNumberField
                      value={l.credit ?? 0}
                      onChangeNumber={(n) => updateLine(idx, { credit: n })}
                      disabled={isLoading}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 56 }}>
                    <IconButton onClick={() => removeLine(idx)} disabled={isLoading}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>

        <InvoiceAccountingEntryPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onAddEntries={handleAddFromPicker}
          userId={userId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceAccountingExampleEditorModal;
